import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
// Lucide React 직접 import - Vite 번들링 최적화 및 초기화 순서 문제 해결
import X from 'lucide-react/dist/esm/icons/x';
import Upload from 'lucide-react/dist/esm/icons/upload';
import Loader2 from 'lucide-react/dist/esm/icons/loader-2';
import styled from '@emotion/styled';
// --- Emotion 스타일 컴포넌트 정의 ---
const UploaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem; /* space-y-4 */
`;
const MediaGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem; /* gap-4 */
`;
const MediaItemWrapper = styled.div`
  position: relative;
  width: 6rem; /* w-24 */
  height: 6rem; /* h-24 */
  border-radius: 0.375rem; /* rounded-md */
  overflow: hidden;
  border: 1px solid var(--theme-border-light, #e5e7eb); /* border border-gray-200 dark:border-gray-700 */

  &:hover button {
    opacity: 1; /* 호버 시 자식 버튼을 보이게 함 */
  }
`;
const MediaElement = styled.img` /* video, img 공통 스타일 */
  height: 100%;
  width: 100%;
  object-fit: cover;
`;
const RemoveButton = styled.button`
  position: absolute;
  top: 0.25rem; /* top-1 */
  right: 0.25rem; /* right-1 */
  padding: 0.25rem; /* p-1 */
  border-radius: 9999px; /* rounded-full */
  background-color: rgba(0, 0, 0, 0.6); /* bg-black/60 */
  color: white;
  opacity: 0;
  transition: opacity 0.15s ease-in-out; /* transition-opacity */

  &:focus {
    outline: none;
    opacity: 1; /* 포커스 시 보이도록 */
    box-shadow: 0 0 0 2px var(--theme-primary-focus-ring);
  }

  svg {
    height: 0.75rem; /* h-3 */
    width: 0.75rem; /* w-3 */
  }
`;
const AddButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 6rem; /* w-24 */
  height: 6rem; /* h-24 */
  border: 2px dashed var(--theme-border, #d1d5db); /* border-2 border-dashed border-gray-300 dark:border-gray-700 */
  border-radius: 0.375rem; /* rounded-md */
  transition: background-color 0.15s ease, border-color 0.15s ease;
  color: var(--theme-text-muted, #6b7280); /* 아이콘/텍스트 색상 */
  background-color: transparent;

  &:not(:disabled):hover {
    border-color: var(--theme-primary, #3b82f6); /* hover:border-blue-500 */
    background-color: var(--theme-primary-faint, #eff6ff); /* hover:bg-blue-50 dark:hover:bg-blue-900/20 */
    color: var(--theme-primary, #3b82f6);
  }

  &:disabled {
    border-color: var(--theme-border-light, #e5e7eb); /* disabled:border-gray-300 dark:border-gray-700 */
    background-color: var(--theme-paper-alt, #f9fafb); /* disabled:bg-gray-100 dark:bg-gray-800 */
    cursor: not-allowed;
    color: var(--theme-text-muted, #9ca3af);
  }

  svg {
    height: 1.25rem; /* h-5 */
    width: 1.25rem; /* w-5 */
    margin-bottom: 0.25rem; /* mb-1 */
    /* 색상은 버튼의 color 상속 */
  }

  span {
    font-size: 0.75rem; /* text-xs */
    /* 색상은 버튼의 color 상속 */
  }
`;
const LoadingSpinner = styled(Loader2)`
  height: 1.25rem; /* h-5 */
  width: 1.25rem; /* w-5 */
  animation: spin 1s linear infinite;

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
const ErrorMessage = styled.p`
  font-size: 0.875rem; /* text-sm */
  color: var(--theme-error, #ef4444); /* text-red-500 */
`;
const HelperText = styled.p`
  font-size: 0.75rem; /* text-xs */
  color: var(--theme-text-muted, #6b7280); /* text-gray-500 dark:text-gray-400 */
`;
/**
 * 프로젝트 미디어 파일 업로드 처리 컴포넌트
 * 
 * 🏗️ FSD Shared Layer - UI Media Upload
 * 이미지와 비디오 파일의 업로드, 미리보기, 삭제 기능을 제공합니다.
 */ const MediaUploader = ({ media, uploadStatus, uploadError, onUpload, onRemove, maxItems = 4, testIds })=>{
    return /*#__PURE__*/ _jsxs(UploaderContainer, {
        "data-testid": testIds?.container,
        children: [
            /*#__PURE__*/ _jsxs(MediaGrid, {
                children: [
                    media.map((item)=>/*#__PURE__*/ _jsxs(MediaItemWrapper, {
                            "data-testid": testIds?.mediaItem,
                            children: [
                                item.type === 'video' ? /*#__PURE__*/ _jsx(MediaElement, {
                                    as: "video",
                                    src: item.url || '',
                                    controls: false,
                                    muted: true,
                                    playsInline: true
                                }) : /*#__PURE__*/ _jsx(MediaElement, {
                                    src: item.url || '',
                                    alt: item.file?.name || `미디어 ${item.id}`
                                }),
                                /*#__PURE__*/ _jsx(RemoveButton, {
                                    type: "button",
                                    onClick: ()=>onRemove(item.id),
                                    "aria-label": "Remove media",
                                    children: /*#__PURE__*/ _jsx(X, {})
                                })
                            ]
                        }, item.id)),
                    media.length < maxItems && /*#__PURE__*/ _jsx(AddButton, {
                        type: "button",
                        onClick: onUpload,
                        disabled: uploadStatus === 'uploading',
                        "data-testid": testIds?.addButton,
                        children: uploadStatus === 'uploading' ? /*#__PURE__*/ _jsx(LoadingSpinner, {}) : /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(Upload, {}),
                                /*#__PURE__*/ _jsx("span", {
                                    children: "파일 추가"
                                })
                            ]
                        })
                    })
                ]
            }),
            uploadError && /*#__PURE__*/ _jsx(ErrorMessage, {
                children: uploadError
            }),
            /*#__PURE__*/ _jsxs(HelperText, {
                children: [
                    "이미지 또는 비디오는 최대 ",
                    maxItems,
                    "개까지 추가 가능합니다. (JPG, PNG, GIF, WebP, MP4, WebM, 각 최대 10MB)"
                ]
            })
        ]
    });
};
export default MediaUploader;
